import {
  cloneElement,
  isValidElement,
  useState,
  type CSSProperties,
  type FocusEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { LabelInput } from "lola-framework-ui-test";

/**
 * Floating label with the same markup as `InputField` in
 * lola-framework-ui-test 0.3.1. Depends on the library classes
 * `lola-input-field`, `lola-input-field--container` and `lola--label-input`.
 * That version ignores `LabelInput`'s `color` and `background` props, so the
 * label color goes through `labelStyle`.
 *
 * Native `<input>` / `<select>` stay as children so autoComplete, inputMode,
 * autoFocus and disabled keep working.
 */
export interface FloatingFieldColors {
  background: string;
  title: string;
  bodyCopy: string;
  inactiveColor: string;
  errorColor?: string;
}

interface FloatingFieldProps {
  label: string;
  value: string;
  required?: boolean;
  isValid?: boolean;
  icon?: ReactNode;
  /** Trailing content inside the field, e.g. a card brand mark. */
  adornment?: ReactNode;
  /**
   * Format hint (e.g. `MM/YY`) shown only while the field is focused and
   * still empty. At rest the floating label occupies that space, so the hint
   * stays hidden until the label has moved up to the border.
   */
  placeholder?: string;
  colors: FloatingFieldColors;
  children: ReactElement<{
    style?: CSSProperties;
    placeholder?: string;
    onFocus?: (event: FocusEvent<HTMLElement>) => void;
    onBlur?: (event: FocusEvent<HTMLElement>) => void;
  }>;
}

/** InputField's default resting border. whiteView slots are often primaryMesh, a gradient. */
const RESTING = "#979797";

const isGradient = (color: unknown): color is string =>
  typeof color === "string" && /gradient/i.test(color);

/** A gradient is a valid ring fill and paints the brand lime. Use a solid instead. */
const solidColor = (color: string, fallback: string) =>
  isGradient(color) ? fallback : color;

const controlBase = (
  padLeft: boolean,
  padRight: boolean,
): CSSProperties => ({
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  padding: `0 ${padRight ? 48 : 14}px 0 ${padLeft ? 44 : 14}px`,
  fontSize: 16,
  lineHeight: "56px",
  outline: "none",
  border: "none",
  background: "transparent",
  fontFamily: "inherit",
});

export const FloatingField = ({
  label,
  value,
  required = false,
  isValid = true,
  icon,
  adornment,
  placeholder,
  colors,
  children,
}: FloatingFieldProps) => {
  const [focused, setFocused] = useState(false);
  const active = focused || value.length > 0;
  const showPlaceholder =
    placeholder !== undefined && focused && value.length === 0;
  const ink = solidColor(colors.bodyCopy, RESTING);
  const placeholderColor = solidColor(colors.inactiveColor, RESTING);
  const raw = !isValid
    ? (colors.errorColor ?? "#dc251c")
    : active
      ? colors.bodyCopy
      : colors.inactiveColor;
  // `title` on a dark partner is the brand mesh, and a focused ring used to
  // paint that lime. Empty, filled and focused stay a solid color.
  const border = solidColor(raw, active ? ink : RESTING);
  const labelColor = solidColor(raw, ink);

  const child = isValidElement(children)
    ? cloneElement(children, {
        style: {
          ...controlBase(Boolean(icon), Boolean(adornment)),
          ...(children.type === "select" ? { appearance: "none" } : {}),
          ...children.props.style,
        },
        ...(placeholder !== undefined
          ? { placeholder: showPlaceholder ? placeholder : "" }
          : {}),
        onFocus: (event: FocusEvent<HTMLElement>) => {
          setFocused(true);
          children.props.onFocus?.(event);
        },
        onBlur: (event: FocusEvent<HTMLElement>) => {
          setFocused(false);
          children.props.onBlur?.(event);
        },
      })
    : children;

  return (
    <div
      className={[
        "lola-input-field",
        icon ? "lola-input-icon" : "lola-input-default",
        active ? "lola-input-active" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <LabelInput
        isActive={active}
        labelStyle={{ color: labelColor }}
      >
        {label}
        {required ? <span className="mock-card-required">*</span> : null}
      </LabelInput>
      <section
        className="lola-input-field--container"
        style={{
          borderRadius: 10,
          ["--bg" as string]: border,
          ["--field-border" as string]: border,
          ["--floating-field-placeholder" as string]: placeholderColor,
        }}
      >
        {placeholder !== undefined ? (
          <style>
            {`.lola-input-field--container > input::placeholder{color:var(--floating-field-placeholder);opacity:1}`}
          </style>
        ) : null}
        {child}
        {adornment ? (
          <span
            className="floating-field-adornment"
            style={{
              position: "absolute",
              right: 14,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              height: 22,
              pointerEvents: "none",
            }}
          >
            <style>
              {`.floating-field-adornment .mock-card-brand{position:static;top:auto;right:auto;transform:none;height:24px;width:auto;object-fit:contain}`}
            </style>
            {adornment}
          </span>
        ) : null}
      </section>
      {icon}
    </div>
  );
};
